import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Reveal, SectionHeading, HeroBanner } from "@/components/Section";
import { useContent } from "@/context/ContentContext";

function normalizeParagraphs(value) {
  const chunks = Array.isArray(value)
    ? value
    : typeof value === "string" && value.trim()
      ? [value]
      : [];
  return chunks.flatMap((chunk) =>
    String(chunk)
      .split(/\n+/)
      .map((s) => s.trim())
      .filter(Boolean)
  );
}

function ParagraphSlider({
  paragraphs = [],
  label = "Paragraph",
  textClassName = "text-slate-500",
  activeDotClassName = "bg-[#3B82F6]",
  inactiveDotClassName = "border border-[#3B82F6] bg-transparent",
}) {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const items = normalizeParagraphs(paragraphs);
  const count = items.length;

  useEffect(() => {
    setIndex(0);
  }, [count]);

  useEffect(() => {
    if (count <= 1 || paused) return undefined;
    const id = setInterval(() => {
      setIndex((i) => (i + 1) % count);
    }, 5000);
    return () => clearInterval(id);
  }, [count, paused]);

  if (count === 0) return null;

  return (
    <div
      className="mt-2 flex w-full flex-1 flex-col items-center"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="min-h-[7rem] w-full flex-1">
        <AnimatePresence mode="wait">
          <motion.p
            key={index}
            initial={{ opacity: 0, x: 12 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -12 }}
            transition={{ duration: 0.28 }}
            className={`relative w-full text-sm leading-snug ${textClassName}`}
          >
            {items[index]}
          </motion.p>
        </AnimatePresence>
      </div>
      {count > 1 && (
        <div className="mt-4 flex items-center gap-2">
          {items.map((_, i) => (
            <button
              key={i}
              type="button"
              aria-label={`${label} ${i + 1}`}
              aria-current={i === index ? "true" : undefined}
              onClick={() => setIndex(i)}
              className={`h-2 w-2 rounded-full transition-colors ${
                i === index ? activeDotClassName : inactiveDotClassName
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default function About() {
  const { content } = useContent();
  const COMPANY = content.company || {};
  const STATS = content.stats || [];
  const TEAM = content.team || [];
  const ABOUT_IMAGE = COMPANY.aboutImage;
  const MISSION_VISION = content.mission_vision || { vision: [], mission: [] };
  const CORE_VALUES = Array.isArray(content.core_values) ? content.core_values : [];
  return (
    <div data-testid="about-page">
      <section className="relative overflow-hidden border-b border-border carbon-texture">
        <HeroBanner src={content.page_banners?.about} />
        <div className="relative mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
          <span className="text-xs font-bold uppercase tracking-[0.3em] text-secondary">
            {content.page_headers?.about_eyebrow || "Our Story"}
          </span>
          <h1 className="mt-4 max-w-3xl font-heading text-5xl font-bold leading-[0.95] tracking-tight text-white md:text-6xl">
            {content.page_headers?.about_title || "Pioneering Automotive Excellence"}
          </h1>
          {content.page_headers?.about_description && (
            <p className="mt-4 max-w-2xl text-lg text-white/70">{content.page_headers.about_description}</p>
          )}
        </div>
      </section>

      {/* Story */}
      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 md:py-28 lg:px-8">
        <div className="grid items-center gap-12 lg:grid-cols-2">
          <Reveal>
            <img
              src={ABOUT_IMAGE}
              alt="Industrial warehouse"
              className="aspect-[4/3] w-full border border-border object-cover"
            />
          </Reveal>
          <Reveal delay={0.1}>
            <span className="text-xs font-bold uppercase tracking-[0.25em] text-primary">
              Established {COMPANY.established}
            </span>
            <h2 className="mt-3 font-heading text-3xl font-bold tracking-tight text-foreground md:text-4xl">
              {content.page_headers?.about_story_title || "A Beacon of Trust & Distinction"}
            </h2>
            <div className="mt-6 space-y-4 text-base leading-relaxed text-muted-foreground">
              <p>{COMPANY.intro}</p>
              <p>
                {COMPANY.storyParagraph2 ||
                  "As the exclusive distributor of Eurogrip tyres in Bangladesh, our company ensures that every product not only meets but exceeds the stringent benchmarks set by the industry. Alongside our distribution of Bajaj lubricants, we reflect an unwavering dedication to optimal engine performance and longevity."}
              </p>
              <p>
                {COMPANY.storyParagraph3 ||
                  "At RTPL, quality is not just a benchmark; it is a way of life. Our customer-centric approach transcends mere transactions, fostering lasting relationships through impeccable service, timely deliveries, and competitive pricing."}
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 md:py-28 lg:px-8">
        <SectionHeading
          eyebrow={content.page_headers?.about_mission_eyebrow || "Our Purpose"}
          title={content.page_headers?.about_mission_title || "Mission & Vision"}
          description={content.page_headers?.about_mission_description}
          align="center"
        />
        <div className="mx-auto mt-4 grid max-w-2xl justify-items-center gap-8 md:grid-cols-2 md:gap-10">
          <Reveal className="w-full">
            <div
              data-testid="mission-card"
              className="mx-auto flex h-full w-full max-w-[18rem] flex-col overflow-hidden rounded-[2rem] bg-white shadow-xl ring-1 ring-black/5 transition-transform hover:-translate-y-1"
            >
              <img
                src="/mission.webp"
                alt=""
                className="block h-auto w-full"
              />
              <div className="relative z-10 -mt-6 flex min-h-[15rem] flex-1 flex-col items-center px-6 pb-10 pt-6 text-center sm:-mt-8 sm:min-h-[16rem] sm:pt-7">
                <h3 className="font-heading text-xl font-bold uppercase tracking-wide text-[#3B82F6]">
                  Mission
                </h3>
                <div className="mt-2 w-14 border-t border-dotted border-[#3B82F6]" />
                <ParagraphSlider
                  paragraphs={MISSION_VISION.mission}
                  label="Mission paragraph"
                  textClassName="text-slate-500"
                  activeDotClassName="bg-[#3B82F6]"
                  inactiveDotClassName="border border-[#3B82F6] bg-transparent"
                />
              </div>
            </div>
          </Reveal>
          <Reveal className="w-full" delay={0.12}>
            <div
              data-testid="vision-card"
              className="mx-auto flex h-full w-full max-w-[18rem] flex-col overflow-hidden rounded-[2rem] bg-white shadow-xl ring-1 ring-black/5 transition-transform hover:-translate-y-1"
            >
              <img
                src="/vision.webp"
                alt=""
                className="block h-auto w-full"
              />
              <div className="relative z-10 -mt-6 flex min-h-[15rem] flex-1 flex-col items-center px-6 pb-10 pt-6 text-center sm:-mt-8 sm:min-h-[16rem] sm:pt-7">
                <h3 className="font-heading text-xl font-bold uppercase tracking-wide text-[#3B82F6]">
                  Vision
                </h3>
                <div className="mt-2 w-14 border-t border-dotted border-[#3B82F6]" />
                <ParagraphSlider
                  paragraphs={MISSION_VISION.vision}
                  label="Vision paragraph"
                  textClassName="text-slate-500"
                  activeDotClassName="bg-[#3B82F6]"
                  inactiveDotClassName="border border-[#3B82F6] bg-transparent"
                />
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Core Values */}
      {CORE_VALUES.length > 0 && (
        <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 md:py-28 lg:px-8" data-testid="core-values-section">
          <SectionHeading
            eyebrow={content.page_headers?.about_values_eyebrow || "What We Stand For"}
            title={content.page_headers?.about_values_title || "Core Values of Runner Group"}
            description={content.page_headers?.about_values_description || "The principles that guide every decision, every product and every partnership at RTPL."}
            align="center"
          />
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {CORE_VALUES.map((v, i) => (
              <Reveal key={i} delay={(i % 3) * 0.08}>
                <div
                  data-testid={`core-value-${i}`}
                  className="group flex h-full items-start gap-5 border border-border bg-card p-6 transition-all hover:-translate-y-1 hover:border-primary hover:shadow-lg md:p-7"
                >
                  {v.image ? (
                    <img
                      src={v.image}
                      alt={v.letter || v.text}
                      className="h-14 w-14 shrink-0 rounded-xl border border-border bg-white object-contain p-1.5 md:h-16 md:w-16"
                    />
                  ) : (
                    <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl bg-primary/10 font-heading text-2xl font-bold text-primary transition-colors group-hover:bg-primary group-hover:text-white md:h-16 md:w-16 md:text-3xl">
                      {v.letter}
                    </span>
                  )}
                  <p className="pt-2 text-base leading-relaxed text-foreground">
                    {v.text}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </section>
      )}

      {/* Group Strength Stats */}
      <section className="border-y border-border bg-card">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
          <SectionHeading
            eyebrow={content.page_headers?.about_stats_eyebrow || "Group Strength"}
            title={content.page_headers?.about_stats_title || "Numbers That Move The Nation"}
            description={content.page_headers?.about_stats_description}
            align="center"
          />
          <div className="grid grid-cols-2 gap-px bg-border md:grid-cols-4">
            {STATS.map((s, i) => (
              <Reveal key={s.label} delay={i * 0.08}>
                <div className="bg-card p-10 text-center">
                  <div className="font-heading text-5xl font-bold text-primary md:text-6xl">
                    {s.value}
                  </div>
                  <div className="mt-3 text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">
                    {s.label}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Leadership */}
      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 md:py-28 lg:px-8">
        <SectionHeading
          eyebrow={content.page_headers?.about_team_eyebrow || "Our Management"}
          title={content.page_headers?.about_team_title || "Leadership Ready To Propel Your Journey"}
          description={content.page_headers?.about_team_description || "We love what we do and we do it with passion — driven by experience and a commitment to excellence."}
          align="center"
        />
        <div className="mx-auto grid max-w-3xl gap-8 md:grid-cols-2">
          {TEAM.map((m, i) => (
            <Reveal key={m.name} delay={i * 0.1}>
              <div
                data-testid={`team-member-${i}`}
                className="group overflow-hidden border border-border bg-card"
              >
                <div className="relative aspect-[3/4] overflow-hidden">
                  <img
                    src={m.image}
                    alt={m.name}
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 flex items-end bg-gradient-to-t from-primary/95 via-primary/50 to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                    <p className="p-6 text-sm leading-relaxed text-white" data-testid={`team-bio-${i}`}>
                      {m.bio}
                    </p>
                  </div>
                  <div className="absolute inset-x-0 bottom-0 h-1 bg-primary" />
                </div>
                <div className="p-6">
                  <h3 className="font-heading text-xl font-bold tracking-tight text-foreground">
                    {m.name}
                  </h3>
                  <span className="mt-1 block text-xs font-bold uppercase tracking-[0.2em] text-primary">
                    {m.role}
                  </span>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>
    </div>
  );
}
